import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ModernButton } from '../ui/ModernButton';
import { describeJobFiles, downloadFile, zipPackages } from '../../utils/jobPackage';
import { collectJobPackage, loadFileData, loadJobSources } from '../../services/jobPackageService';

/**
 * Every file of one job's export, to download one by one or as a ZIP.
 *
 * Lists all seven kinds, available or not, so a missing file shows as missing
 * instead of the list quietly being shorter than the export was.
 */
export function JobFilesModal({ job, onClose }) {
  const [sources, setSources] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadJobSources(job).then((result) => {
      if (cancelled) return;
      if (result.error) toast.error(result.error);
      setSources(result);
    });
    return () => {
      cancelled = true;
    };
  }, [job]);

  const files = sources ? describeJobFiles({ job, ...sources }) : [];
  const available = files.filter((file) => file.available);
  const withoutOriginals = sources && !sources.documents?.baseName;

  async function handleFile(file) {
    setBusy(file.kind);
    try {
      downloadFile(await loadFileData(file, { job, ...sources }), file.filename, file.type);
    } catch (error) {
      toast.error(`${file.label} konnte nicht geladen werden: ${error.message}`);
    } finally {
      setBusy(null);
    }
  }

  async function handleZip() {
    setBusy('zip');
    const pack = await collectJobPackage(job, sources);
    setBusy(null);

    if (pack.files.length === 0) {
      toast.error('Keine Datei konnte geladen werden.');
      return;
    }
    downloadFile(zipPackages([pack]), `${pack.baseName}.zip`, 'application/zip');
    if (pack.failed.length > 0) {
      toast.error(`${pack.failed.length} Datei(en) fehlen im ZIP: ${pack.failed[0].filename}`);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
      <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 sm:px-8 sm:pt-8">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Dateien herunterladen</h2>
            <p className="truncate text-sm text-slate-500 dark:text-slate-400">
              {job.title}
              {job.role && <span className="italic"> · {job.role}</span>}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pb-6 sm:px-8 sm:pb-8">
          {!sources ? (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">Wird geladen …</p>
          ) : (
            <>
              <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                {files.map((file) => (
                  <li key={file.kind} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 dark:text-slate-200">{file.label}</p>
                      <p
                        className={`truncate font-mono text-xs ${
                          file.available ? 'text-slate-500 dark:text-slate-400' : 'text-slate-400 dark:text-slate-500 line-through'
                        }`}
                      >
                        {file.filename}
                      </p>
                    </div>
                    {file.available ? (
                      <button
                        type="button"
                        onClick={() => handleFile(file)}
                        disabled={busy !== null}
                        className="shrink-0 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:text-slate-400 disabled:no-underline"
                      >
                        {busy === file.kind ? 'Lädt …' : 'Herunterladen'}
                      </button>
                    ) : (
                      <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">fehlt</span>
                    )}
                  </li>
                ))}
              </ul>

              {withoutOriginals && (
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  JSON und Storyboard kommen erst mit dem nächsten ZIP-Import dieser Stelle.
                </p>
              )}

              <div className="mt-6 flex justify-end gap-2">
                <ModernButton variant="secondary" onClick={onClose}>
                  Schließen
                </ModernButton>
                <ModernButton
                  onClick={handleZip}
                  disabled={busy !== null || available.length === 0}
                  loading={busy === 'zip'}
                >
                  Alle als ZIP
                </ModernButton>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
